import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Briefcase,
  Phone,
  Mail,
  MapPin,
  Edit,
  Trash2,
  IndianRupee,
  FileText,
  Calendar,
  X,
} from "lucide-react";
import { ContractorDashboardTab } from "./ContractorDashboardTab";
import { ContractorWorkOrdersTab } from "./ContractorWorkOrdersTab";
import { ContractorPaymentsTab } from "./ContractorPaymentsTab";
import { ContractorAttendanceTab } from "./ContractorAttendanceTab";
import { ContractorApi } from "../../../api/contractors/service";

// Utility for classes
const cn = (...classes) => classes.filter(Boolean).join(" ");

export function ContractorProfile({
  contractor,
  workOrders,
  payments,
  contractors,
  onBack,
  onAddPayment,
  onCreateWorkOrder,
  onViewWorkOrder,
  onUpdated,
  onDeleted,
}) {
  const [activeTab, setActiveTab] = useState("workorders");
  const [contractorInfo, setContractorInfo] = useState(contractor);
  const [editValues, setEditValues] = useState({
    name: contractor?.name || "",
    contact: contractor?.contact || "",
    address: contractor?.address || "",
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editError, setEditError] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const currentContractor = contractorInfo || contractor || {};

  const formatCurrency = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    }
    return `₹${amount.toLocaleString()}`;
  };

  const Button = ({ variant = "default", size = "default", className = "", children, ...props }) => {
    const baseStyles = "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";
    const variants = {
      default: "bg-sm-primary text-white shadow hover:bg-sm-primary/90",
      destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
      outline: "border border-input bg-transparent shadow-sm hover:bg-sm-accent hover:text-sm-accent-foreground",
      secondary: "bg-sm-secondary text-sm-secondary-foreground shadow-sm hover:bg-sm-secondary/80",
      ghost: "hover:bg-sm-accent hover:text-sm-accent-foreground",
      link: "text-sm-primary underline-offset-4 hover:underline",
    };
    const sizes = {
      default: "h-9 px-4 py-2",
      sm: "h-8 rounded-md px-3 text-xs",
      lg: "h-10 rounded-md px-8",
      icon: "h-9 w-9",
    };
    return (
      <button className={cn(baseStyles, variants[variant], sizes[size], className)} {...props}>
        {children}
      </button>
    );
  };

  const Badge = ({ variant = "default", className = "", children }) => {
    const baseStyles = "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";
    const variants = {
      default: "border-transparent bg-sm-primary text-sm-primary-foreground hover:bg-sm-primary/80",
      secondary: "border-transparent bg-sm-secondary/10 text-sm-secondary hover:bg-sm-secondary/20",
      destructive: "border-transparent bg-destructive/10 text-destructive hover:bg-destructive/20",
      outline: "text-foreground",
    };
    return (
      <div className={cn(baseStyles, variants[variant], className)}>
        {children}
      </div>
    );
  };

  const Card = ({ className = "", children }) => (
    <div className={`rounded-lg border bg-white text-card-foreground shadow-sm ${className}`}>
      {children}
    </div>
  );

  const CardContent = ({ className = "", children }) => (
    <div className={`p-6 ${className}`}>{children}</div>
  );

  useEffect(() => {
    setContractorInfo(contractor);
    setEditValues({
      name: contractor?.name || "",
      contact: contractor?.contact || "",
      address: contractor?.address || "",
    });
  }, [contractor]);

  const handleUpdateContractor = async () => {
    setIsSavingEdit(true);
    setEditError("");
    try {
      const payload = {
        name: editValues.name,
        contact: editValues.contact,
        address: editValues.address,
      };
      const res = await ContractorApi.updateContractor(contractor.id, payload);
      const data = res?.data || res;
      if (data) {
        setContractorInfo((prev) => ({ ...prev, ...payload }));
        onUpdated?.(payload);
        setShowEditModal(false);
      } else {
        setEditError("Failed to update contractor");
      }
    } catch (err) {
      setEditError(err?.message || "Failed to update contractor");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    setDeleteError("");
    try {
      await ContractorApi.deleteById(contractor.id);
      onDeleted?.(contractor.id);
      onBack?.();
    } catch (err) {
      setDeleteError(err?.message || "Failed to delete contractor");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="h-full flex flex-col gap-5 p-5 overflow-auto w-[85%]">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="h-9 w-9"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-sm-secondary/10 flex items-center justify-center">
              <Briefcase className="h-6 w-6 text-sm-secondary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-semibold text-foreground">{currentContractor.name}</h1>
                <Badge variant="secondary" className="text-xs font-normal bg-sm-secondary/10 text-sm-secondary border-0">
                  {currentContractor.category}
                </Badge>
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground mt-0.5">
                <span className="flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5" />
                  {currentContractor.contact}
                </span>
                <span className="flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" />
                  {currentContractor.address}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 border-border/60"
            onClick={() => setShowEditModal(true)}
          >
            <Edit className="h-3.5 w-3.5" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-destructive hover:text-destructive border-border/60"
            onClick={() => setShowDeleteModal(true)}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </Button>
        </div>
      </div>

      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <p className="text-base font-semibold text-gray-900">Edit Contractor</p>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              {editError && (
                <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 border border-red-100">
                  {editError}
                </div>
              )}
              {[
                { label: "Name", key: "name" },
                { label: "Contact Number", key: "contact" },
                { label: "Address", key: "address" },
              ].map((field) => (
                <div key={field.key} className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">{field.label}</label>
                  <input
                    type="text"
                    value={editValues[field.key]}
                    onChange={(e) =>
                      setEditValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-sm-primary focus:outline-none focus:ring-1 focus:ring-sm-primary"
                  />
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-3 bg-gray-50">
              <button
                onClick={() => setShowEditModal(false)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                disabled={isSavingEdit}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateContractor}
                className="rounded-md bg-sm-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-sm-primary/90 transition-colors disabled:opacity-70"
                disabled={
                  isSavingEdit ||
                  !String(editValues.name || "").trim() ||
                  !String(editValues.contact || "").trim()
                }
              >
                {isSavingEdit ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="w-full max-w-sm rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <p className="text-base font-semibold text-gray-900">Delete Contractor</p>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="space-y-3 px-6 py-4 text-sm text-gray-700">
              <p>Are you sure you want to delete this contractor? This action cannot be undone.</p>
              {deleteError && (
                <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 border border-red-100">
                  {deleteError}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-3 bg-gray-50">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-white hover:bg-destructive/90 transition-colors disabled:opacity-70"
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-border/60 shadow-none">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-sm-secondary/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-sm-secondary" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-foreground">{formatCurrency(contractor.totalWorkOrderValue)}</p>
              <p className="text-xs text-muted-foreground">Total Work Order Value</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/60 shadow-none">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-foreground">{formatCurrency(contractor.totalPaid)}</p>
              <p className="text-xs text-muted-foreground">Total Paid</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/60 shadow-none">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-red-500/10 flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-foreground">{formatCurrency(contractor.totalDue)}</p>
              <p className="text-xs text-muted-foreground">Total Due</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/60 shadow-none">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-sm-primary/10 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-sm-primary" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-foreground">{contractor.activeProjects}</p>
              <p className="text-xs text-muted-foreground">Active Projects</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex-1 flex flex-col">
        <div className="h-10 p-1 w-fit bg-[#F2F1F3] rounded-lg flex items-center space-x-1">
          {["dashboard", "workorders", "payments", "attendance"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={cn(
                "h-8 text-sm px-4 rounded-md transition-all font-medium",
                activeTab === tab
                  ? "bg-[#FDFDFD] shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div className="flex-1 mt-4">
          {activeTab === "dashboard" && (
            <ContractorDashboardTab
              contractor={contractor}
              workOrders={workOrders}
              payments={payments}
            />
          )}
          {activeTab === "workorders" && (
            <ContractorWorkOrdersTab
              workOrders={workOrders}
              contractors={contractors}
              selectedContractorId={contractor.id}
              onCreateWorkOrder={onCreateWorkOrder}
              onViewWorkOrder={onViewWorkOrder}
            />
          )}
          {activeTab === "payments" && (
            <ContractorPaymentsTab
              payments={payments}
              workOrders={workOrders}
              contractor={contractor}
              onAddPayment={onAddPayment}
            />
          )}
          {activeTab === "attendance" && (
            <ContractorAttendanceTab
              contractor={contractor}
              workOrders={workOrders}
            />
          )}
        </div>
      </div>
    </div>
  );
}

